import { normalizeAuthStatus, normalizeCloud, normalizeLocalRecording, normalizeNetwork, normalizeStatus } from './adapters/agent';
import {
  INVITE_PREFIX,
  memberFromInvite,
  normalizeAlert,
  normalizeAudit,
  normalizeChannels,
  fleetState,
  normalizeEvent,
  normalizeFleetDevice,
  normalizeMe,
  normalizeMember,
  normalizePreset,
  normalizeRecording,
  normalizeRelease,
  normalizeRollout,
  normalizeSchedule,
  normalizeScheduleState,
} from './adapters/cloud';
import type {
  AlertChannel,
  AlertChannelIn,
  AlertChannels,
  AlertDelivery,
  AlertPrefs,
  PushSubscriptionIn,
  TelemetryPoint,
  PushSubscriptionOut,
  VapidOut,
  AlertRow,
  CaptionSchedule,
  CaptionScheduleIn,
  CaptionScheduleState,
  AuditEntry,
  AuthStatus,
  CloudLinkStatus,
  Device,
  DeviceId,
  DeviceStatus,
  DisplaySettings,
  ExportPayload,
  ExportResult,
  KeytermBudget,
  LogLine,
  ConsoleRole,
  Me,
  Member,
  MemberScope,
  NetworkStatus,
  Preset,
  PresetApplyResult,
  Recording,
  Release,
  Role,
  Rollout,
  SecurityStatus,
  Settings,
  SettingsPatch,
  SettingsPatchResult,
  ShowEvent,
  SystemInfo,
  UpdateStatus,
  WordLibrary,
} from '@glyph/protocol-ts';
import type { CommandClient } from './client/types';
import { CommandError } from './client/types';

const asArray = <T>(r: any, ...keys: string[]): T[] => {
  if (Array.isArray(r)) return r as T[];
  for (const k of keys) if (Array.isArray(r?.[k])) return r[k] as T[];
  return [];
};

export interface UpdateCheckOut {
  channel: string;
  newest: string | null;
  current: string;
  available: boolean;
  offered: boolean;
  message: string;
}
export interface HotspotDefault {
  password: string | null;
  units_following: number;
  units_custom: number;
  /** Units that do not have the password yet (offline, or not sent yet). */
  units_waiting: number;
}
export interface UnitHotspot {
  password: string | null;
  source: 'default' | 'custom' | 'none';
  status: 'applied' | 'waiting' | 'sending' | 'failed' | 'none';
  detail: string | null;
}

/** Body of a cloud preset create / update. Wi-Fi password left out on an update keeps the stored one. */
export interface PresetIn {
  name: string;
  description?: string;
  settings?: Record<string, Record<string, unknown>>;
  wifi?: { ssid: string; password?: string | null; hidden?: boolean }[];
}

export interface LockUpdatesOut {
  locked: boolean;
  results: {
    device_id: DeviceId;
    status: 'sent' | 'queued' | 'error' | 'forbidden' | string;
    cmd_id: string | null;
    error: string | null;
  }[];
}

/** Fleet "Apply look": copy one unit's caption look (display section) to many units. */
export interface LookPushIn {
  source_device_id: DeviceId;
  /** A preset saved on the source unit; omit for its current look. */
  preset?: string | null;
  /** Also keep the look as a preset of this name on each target (units whose software can store pushed presets). */
  save_as?: string | null;
  device_ids?: DeviceId[];
  all?: boolean;
}
export interface LookPushOut {
  source_device_id: DeviceId;
  preset: string | null;
  sections: string[];
  results: (LockUpdatesOut['results'][number] & { saved: boolean })[];
}

/** Build a fleet `Device` row from a local status.get (the local shell has exactly one device). */
export function deviceFromStatus(st: DeviceStatus, settings?: Settings | null): Device {
  const name = settings?.device?.name ? `Glyph ${settings.device.name}` : 'Glyph G1';
  return {
    id: st.cloud?.device_id ?? 'self',
    serial: '',
    name,
    status: 'active',
    state: st.engine?.locked
      ? 'locked'
      : fleetState(true, !!st.engine?.locked, st.update?.state ?? (st.system_state === 'updating' ? 'installing' : st.system_state === 'downloading' ? 'downloading' : null), !!st.engine?.running),
    online: true,
    last_seen: st.ts,
    agent_version: st.version?.agent ?? '',
    slot: st.version?.slot ?? 'A',
    org_id: '',
    assignment: st.cloud?.assignment ?? null,
    asr_model: st.engine?.model ?? '—',
    dg_rtt_ms: st.engine?.rtt_ms ?? null,
    cloud_rtt_ms: null,
    update_state: st.update?.state ?? null,
    update_target: st.update?.target_version || st.update?.staged_version || null,
    update_pct: st.update?.progress_pct ?? null,
    update_error: st.update?.last_error ?? null,
    update_finished_at: st.update?.finished_at ?? null,
    os_version: (st.version?.os ?? '').split('-')[0] ?? '',
    audio_dbfs: st.stats?.audio_dbfs?.[0] ?? null,
    uptime_s: st.stats?.uptime_s ?? 0,
    address: st.network?.address ?? null,
    uplink: st.network?.uplink ?? 'none',
    hotspot_ssid: st.network?.ap?.ssid ?? null,
    enrolled_at: st.cloud?.identity?.enrolled_at ?? 0,
    key_id: st.cloud?.identity?.key_id ?? '',
    locked: !!st.engine?.locked,
    update_locked: st.update?.locked ?? null,
  };
}

/**
 * Logical API the hooks consume. Device-scoped reads/writes go through the command dispatcher (every
 * device action is a command in PLAN); fleet resources (devices, members, releases, rollouts, alerts, audit,
 * me) are cloud REST. The local shell maps the handful of REST paths it has onto `/api/v1`.
 */
export function createApi(client: CommandClient) {
  const local = client.kind === 'local';
  const r = client.rest;
  const enc = encodeURIComponent;
  const dev = (id: DeviceId) => (local ? '' : `/devices/${enc(id)}`);
  const cmd = client.cmd.bind(client);
  // Cloud: `me` carries the CSRF token and the org the console acts in (members, invites, events);
  // recordings are addressed by id in the cloud but by file name in the screens.
  let orgId: string | null = null;
  const recordingIds = new Map<string, string>(); // `${device}/${name}` -> recording id
  const rememberMe = (raw: unknown): Me => {
    const me = normalizeMe(raw);
    orgId = me.org.id || null;
    client.setCsrf?.((raw as { csrf_token?: string | null })?.csrf_token ?? null);
    return me;
  };
  const org = async (): Promise<string> => {
    if (!orgId) rememberMe(await r.get('/auth/me'));
    if (!orgId) throw new CommandError('no organisation membership', 'forbidden', 403);
    return orgId;
  };
  const recId = async (id: DeviceId, name: string): Promise<string> => {
    const k = `${id}/${name}`;
    if (!recordingIds.has(k)) await listRecordings(id);
    const rid = recordingIds.get(k);
    if (!rid) throw new CommandError(`recording ${name} not found`, 'rejected', 404);
    return rid;
  };
  const listEvents = async (): Promise<ShowEvent[]> => (await r.get<unknown[]>(`/orgs/${enc(await org())}/events`)).map(normalizeEvent);
  // DeviceOut carries only the event id; the show name comes from the org's event list.
  const withShowName = (d: Device, shows: ShowEvent[]): Device =>
    d.assignment?.event_id && !d.assignment.event_name
      ? { ...d, assignment: { ...d.assignment, event_name: shows.find((e) => e.id === d.assignment!.event_id)?.name ?? null } }
      : d;
  const listRecordings = async (id?: DeviceId): Promise<Recording[]> => {
    const raw = await r.get<unknown[]>(id ? `/recordings?device_id=${enc(id)}` : '/recordings');
    const out = raw.map(normalizeRecording);
    for (const rec of out) recordingIds.set(`${rec.device_id}/${rec.name}`, rec.id);
    return out;
  };

  return {
    kind: client.kind,
    // ---- session ----------------------------------------------------------------------------------
    me: async () => (local ? r.get<Me>('/me') : rememberMe(await r.get('/auth/me'))),
    authStatus: async (id: DeviceId = 'self') => normalizeAuthStatus(await cmd(id, 'auth.status', {})),
    unlock: (id: DeviceId, pin: string) =>
      cmd(id, 'auth.unlock', { pin }) as Promise<{ token: string; expires_in_s: number }>,
    /** Local account sign-in (admin | tech) -> session token + role. */
    localLogin: (id: DeviceId, username: string, password: string) =>
      cmd(id, 'auth.login', { username, password }) as Promise<{ token: string; role: 'admin' | 'tech' }>,
    /** Cloud sign-in. A user with TOTP enabled gets `totp_required` back and the code completes the login. */
    login: async (email: string, password: string, code: string): Promise<Me> => {
      // `login` is an email or a username (cloud/glyph_cloud/api/schemas.py LoginIn)
      const out = await r.post<{ ok: boolean; totp_required: boolean; csrf_token?: string | null; user?: unknown }>('/auth/login', { login: email, password });
      if (out.totp_required) {
        if (!code) throw new CommandError('Authenticator code required', 'forbidden', 401);
        const v = await r.post<{ ok: boolean; csrf_token?: string | null; user?: unknown }>('/auth/totp/verify', { code });
        return rememberMe({ ...(v.user as object), csrf_token: v.csrf_token });
      }
      return rememberMe({ ...(out.user as object), csrf_token: out.csrf_token });
    },
    /** Invite link (`/invite?token=`): creates the account when the email is new, then signs in. */
    acceptInvite: async (token: string, name: string, password: string): Promise<Me> => {
      const out = await r.post<{ ok: boolean; totp_required: boolean; csrf_token?: string | null; user?: unknown }>('/invites/accept', { token, name, password: password || null });
      if (out.totp_required) throw new CommandError('Account has two-factor enabled: sign in with your authenticator code', 'forbidden', 401);
      return rememberMe({ ...(out.user as object), csrf_token: out.csrf_token });
    },
    logout: () => (local ? cmd('self', 'auth.logout', {}) : r.post<void>('/auth/logout')),

    // ---- fleet ------------------------------------------------------------------------------------
    devices: async (params?: { state?: string; q?: string }): Promise<Device[]> => {
      if (local) {
        const st = normalizeStatus(await cmd('self', 'status.get', {}));
        const settings = (await cmd('self', 'settings.get', {})).settings as unknown as Settings;
        return [deviceFromStatus(st, settings)];
      }
      const qs = new URLSearchParams();
      if (params?.q) qs.set('q', params.q);
      const q = qs.toString();
      const [raw, shows] = await Promise.all([r.get<unknown[]>(`/devices${q ? `?${q}` : ''}`), listEvents().catch(() => [] as ShowEvent[])]);
      const list = raw.map(normalizeFleetDevice).map((d) => withShowName(d, shows));
      return params?.state ? list.filter((d) => d.state === params.state) : list;
    },
    /** Rename (operator+). Cloud: the fleet name, pushed to the unit as `device.rename` (queued while offline). */
    renameDevice: async (id: DeviceId, name: string): Promise<Device> => {
      if (!local) {
        const [raw, shows] = await Promise.all([r.post(`/devices/${enc(id)}/rename`, { name }), listEvents().catch(() => [] as ShowEvent[])]);
        return withShowName(normalizeFleetDevice(raw), shows);
      }
      await cmd('self', 'device.rename', { name });
      const st = normalizeStatus(await cmd('self', 'status.get', {}));
      const settings = (await cmd('self', 'settings.get', {})).settings as unknown as Settings;
      return deviceFromStatus(st, settings);
    },
    device: async (id: DeviceId): Promise<Device> => {
      if (!local) {
        const [raw, shows] = await Promise.all([r.get(`/devices/${enc(id)}`), listEvents().catch(() => [] as ShowEvent[])]);
        return withShowName(normalizeFleetDevice(raw), shows);
      }
      const st = normalizeStatus(await cmd('self', 'status.get', {}));
      const settings = (await cmd('self', 'settings.get', {})).settings as unknown as Settings;
      return deviceFromStatus(st, settings);
    },

    // ---- device-scoped (commands) ----------------------------------------------------------------
    status: async (id: DeviceId) => normalizeStatus(await cmd(id, 'status.get', {})),
    settings: async (id: DeviceId) => (await cmd(id, 'settings.get', {})).settings as unknown as Settings,
    /** The two secrets an operator may need to see again (hotspot password, PIN); the agent reveals them only to an authenticated local session. */
    secrets: async (id: DeviceId) =>
      (await cmd(id, 'settings.get', { reveal: true, sections: ['auth', 'network'] }))
        .settings as unknown as {
        auth?: { pin?: string; admin_password?: string; tech_password?: string };
        network?: { ap_password?: string };
      },
    patchSettings: (id: DeviceId, patch: SettingsPatch) =>
      cmd(id, 'settings.patch', {
        patch: patch as Record<string, Record<string, unknown>>,
      }) as Promise<SettingsPatchResult>,
    profiles: async (id: DeviceId) =>
      asArray<string | { name: string }>(
        await cmd(id, 'settings.profiles.list', {}),
        'profiles',
        'names',
      ).map((p) => (typeof p === 'string' ? p : p.name)),
    profileSave: (id: DeviceId, name: string) => cmd(id, 'settings.profiles.save', { name }),
    profileLoad: (id: DeviceId, name: string) => cmd(id, 'settings.profiles.load', { name }),
    previewDisplay: (id: DeviceId, style: Partial<DisplaySettings>) =>
      cmd(id, 'display.preview', { style, ttl_s: 30 }),
    network: async (id: DeviceId) => normalizeNetwork(await cmd(id, 'network.status', {})),
    updateStatus: async (id: DeviceId) => (await cmd(id, 'update.status', {})).status as UpdateStatus,
    logs: async (id: DeviceId, n = 200) =>
      asArray<LogLine>(await cmd(id, 'logs.tail', { n }), 'lines', 'entries', 'logs'),
    wordLibrary: async (id: DeviceId) => {
      const res = await cmd(id, 'wordlib.get', {});
      return {
        library: res.library as unknown as WordLibrary,
        budget: res.keyterm_budget as unknown as KeytermBudget,
      };
    },
    setWordLibrary: (id: DeviceId, lib: WordLibrary) =>
      cmd(id, 'wordlib.set', {
        library_id: lib.library_id,
        version: lib.version,
        keyterms: lib.keyterms as unknown as Record<string, unknown>[],
        replacements: lib.replacements,
        blocked: lib.blocked,
        blocked_mode: lib.blocked_mode,
        vulgarity_filter: lib.vulgarity_filter,
        vulgarity_custom: lib.vulgarity_custom,
      }),
    cloudStatus: async (id: DeviceId) => normalizeCloud(await cmd(id, 'cloud.status', {})),
    systemInfo: (id: DeviceId) => cmd(id, 'system.info', {}) as Promise<SystemInfo>,
    security: (id: DeviceId) => r.get<SecurityStatus>(`${dev(id)}/security`),

    // ---- recordings -------------------------------------------------------------------------------
    recordings: async (id?: DeviceId): Promise<Recording[]> => {
      if (local) return asArray<unknown>(await cmd('self', 'record.sessions', {}), 'sessions', 'files').map(normalizeLocalRecording);
      return listRecordings(id);
    },
    renameRecording: async (id: DeviceId, name: string, display_name: string) =>
      local
        ? cmd('self', 'files.rename', { name, display_name })
        : normalizeRecording(await r.patch(`/recordings/${enc(await recId(id, name))}`, { display_name })),
    deleteRecording: async (id: DeviceId, name: string): Promise<void> => {
      if (local) await cmd('self', 'record.delete', { name });
      else await r.del<void>(`/recordings/${enc(await recId(id, name))}`);
    },
    exportRecording: (id: DeviceId, payload: ExportPayload) =>
      cmd(id, 'export.run', payload) as Promise<ExportResult>,

    // ---- alerts -----------------------------------------------------------------------------------
    alerts: async (open?: boolean): Promise<AlertRow[]> => {
      if (local) {
        const list = asArray<AlertRow>(await cmd('self', 'alerts.list', {}), 'alerts');
        return open ? list.filter((a) => !a.acked_at) : list;
      }
      // the API lists open + acked by default; `open` here means "needs attention" = not yet acked
      return (await r.get<unknown[]>(`/alerts${open ? '?status_=open' : ''}`)).map(normalizeAlert);
    },
    ackAlert: async (a: AlertRow) =>
      local ? cmd('self', 'alerts.ack', { key: a.key }) : normalizeAlert(await r.post(`/alerts/${enc(a.id)}/ack`)),
    /** Web Push for this browser (cloud only). */
    pushVapidKey: () => r.get<VapidOut>('/push/vapid-public-key'),
    pushSubscribe: (body: PushSubscriptionIn) => r.post<PushSubscriptionOut>('/push/subscriptions', body),
    pushUnsubscribe: (endpoint: string) => r.post<{ ok: boolean }>('/push/unsubscribe', { endpoint }),
    /** Per-device notification arming (cloud only). */
    alertPrefs: (id: DeviceId) => r.get<AlertPrefs>(`/devices/${enc(id)}/alert-prefs`),
    setAlertPrefs: (id: DeviceId, body: Pick<AlertPrefs, 'arm_mode' | 'snooze_until' | 'muted_kinds'>) => r.put<AlertPrefs>(`/devices/${enc(id)}/alert-prefs`, body),
    alertChannels: async (): Promise<AlertChannels> =>
      local ? r.get<AlertChannels>('/alerts/channels') : normalizeChannels(await r.get(`/alerts/channels?org_id=${enc(await org())}`)),
    /** Routing targets as rows (cloud): list, add, change, remove, send a test email, and whether email can be sent at all. */
    channels: async (): Promise<AlertChannel[]> => (local ? [] : r.get<AlertChannel[]>(`/alerts/channels?org_id=${enc(await org())}`)),
    createChannel: async (body: AlertChannelIn): Promise<AlertChannel> => r.post<AlertChannel>(`/alerts/channels?org_id=${enc(await org())}`, body),
    updateChannel: (id: string, body: AlertChannelIn): Promise<AlertChannel> => r.put<AlertChannel>(`/alerts/channels/${enc(id)}`, body),
    deleteChannel: (id: string): Promise<void> => r.del<void>(`/alerts/channels/${enc(id)}`),
    testChannel: (id: string): Promise<{ sent: boolean; detail: string }> => r.post(`/alerts/channels/${enc(id)}/test`),
    alertDelivery: (): Promise<AlertDelivery> => (local ? Promise.resolve({ email_configured: false, email_from: '' }) : r.get<AlertDelivery>('/alerts/delivery')),

    // ---- releases / rollouts ----------------------------------------------------------------------
    releases: async (channel: 'stable' | 'beta' = 'stable', kind: 'app' | 'os' = 'app'): Promise<Release[]> =>
      (await r.get<unknown[]>(`/releases?channel=${channel}&kind=${kind}`)).map(normalizeRelease),
    rollout: async (id: string): Promise<Rollout> => {
      const [raw, devices, releases] = await Promise.all([
        r.get<{ release_id?: string }>(`/rollouts/${enc(id)}`),
        r.get<unknown[]>(`/rollouts/${enc(id)}/devices`),
        r.get<unknown[]>('/releases?include_yanked=true'),
      ]);
      const rel = releases.map(normalizeRelease).find((x) => x.id === raw.release_id);
      return normalizeRollout(raw, devices, rel?.version ?? '');
    },
    rollouts: async (): Promise<Rollout[]> => {
      const [raw, releases] = await Promise.all([r.get<unknown[]>('/rollouts'), r.get<unknown[]>('/releases?include_yanked=true')]);
      const versions = new Map(releases.map(normalizeRelease).map((x) => [x.id, x.version]));
      return raw.map((x) => normalizeRollout(x, [], versions.get(String((x as { release_id?: string }).release_id)) ?? ''));
    },
    /** Lock / unlock updates on many devices: cloud fans out `settings.patch {update:{lock}}` (queued when offline). */
    /** Telemetry history of one unit (30 s rows). Cloud only; the local shell has no history store. */
    telemetry: async (id: DeviceId, sinceMinutes = 60): Promise<TelemetryPoint[]> => (local ? [] : r.get<TelemetryPoint[]>(`/devices/${enc(id)}/telemetry?since_minutes=${sinceMinutes}&limit=6000`)),
    /** Push a Deepgram key to all or selected units (admin). Local shell: this unit only. */
    sendDeepgramKey: async (body: { api_key: string; device_ids?: DeviceId[]; all?: boolean }): Promise<{ results: LockUpdatesOut['results'] }> => {
      if (local) {
        await cmd('self', 'credentials.deepgram.set', { api_key: body.api_key });
        return { results: [{ device_id: 'self', status: 'sent', cmd_id: null, error: null }] };
      }
      return r.post<{ results: LockUpdatesOut['results'] }>('/devices/deepgram-key', body);
    },
    /** Copy one unit's caption look to the chosen units (operator+): the cloud reads the source and fans the display
     * section out through the queueable command bus. Fleet only; the local shell is one unit. */
    pushLook: async (body: LookPushIn): Promise<LookPushOut> => {
      if (local) throw new Error('Apply look needs the fleet console');
      return r.post<LookPushOut>('/devices/look', body);
    },
    lockUpdates: async (body: {
      device_ids?: DeviceId[];
      all?: boolean;
      locked: boolean;
    }): Promise<LockUpdatesOut> => {
      if (local) {
        await cmd('self', 'settings.patch', { patch: { update: { lock: body.locked } } });
        return {
          locked: body.locked,
          results: [{ device_id: 'self', status: 'sent', cmd_id: null, error: null }],
        };
      }
      return r.post<LockUpdatesOut>('/devices/lock-updates', body);
    },
    /** One-phase rollout (the console has no staged phases yet), started straight away. */
    createRollout: async (body: {
      release_id: string;
      target: { kind: 'all' | 'devices'; ids: DeviceId[] };
      policy: 'immediate' | 'skip_if_engine_running';
    }): Promise<Rollout> => {
      const created = await r.post<{ id: string; release_id: string }>('/rollouts', {
        release_id: body.release_id,
        target: body.target.kind === 'devices' ? { kind: 'devices', device_ids: body.target.ids } : { kind: 'all' },
        phases: [{ percent: 100, min_success_pct: 0, hold_s: 0 }],
        policy: { skip_if_engine_running: body.policy === 'skip_if_engine_running', deadline_hours: 24, allow_downgrade: false },
      });
      const started = await r.post(`/rollouts/${enc(created.id)}/start`);
      return normalizeRollout(started, [], '');
    },

    // ---- org --------------------------------------------------------------------------------------
    members: async (): Promise<Member[]> => {
      const o = await org();
      const [members, invites] = await Promise.all([r.get<unknown[]>(`/orgs/${enc(o)}/members`), r.get<unknown[]>(`/orgs/${enc(o)}/invites`)]);
      const pending = invites.filter((i) => {
        const x = i as { accepted_at?: string | null; revoked_at?: string | null };
        return !x.accepted_at && !x.revoked_at;
      });
      return [...members.map(normalizeMember), ...pending.map(memberFromInvite)];
    },
    /** The invite link is returned once (the API emails it too when an email provider is configured). */
    invite: async (email: string, role: Role): Promise<Member & { invite_url: string | null }> => {
      const raw = await r.post<{ token?: string }>(`/orgs/${enc(await org())}/invites`, { email, role });
      const origin = typeof location !== 'undefined' ? location.origin : '';
      return { ...memberFromInvite(raw), invite_url: raw.token ? `${origin}/invite?token=${encodeURIComponent(raw.token)}` : null };
    },
    setRole: async (memberId: string, role: ConsoleRole): Promise<Member> => normalizeMember(await r.put(`/orgs/${enc(await org())}/members/${enc(memberId)}`, { role })),
    /** Admin-created account with a username and password to hand out (no invite). Room operators and event admins
     * need a scope (units and/or groups); so does every account an event admin creates. */
    createMember: async (body: { username: string; password: string; name?: string; role: ConsoleRole; scope?: MemberScope | null }): Promise<Member> =>
      normalizeMember(await r.post(`/orgs/${enc(await org())}/members`, body)),
    /** Which units and groups a member may see and act on (empty both = whole org for viewers/operators). */
    setMemberScope: async (memberId: string, scope: MemberScope): Promise<Member> =>
      normalizeMember(await r.put(`/orgs/${enc(await org())}/members/${enc(memberId)}/scope`, scope)),
    /** Name / username / password set by an admin. */
    patchMember: async (memberId: string, body: { name?: string; username?: string; password?: string }): Promise<Member> =>
      normalizeMember(await r.put(`/orgs/${enc(await org())}/members/${enc(memberId)}`, body)),
    removeMember: async (memberId: string): Promise<void> => {
      const o = await org();
      if (memberId.startsWith(INVITE_PREFIX)) await r.del<void>(`/orgs/${enc(o)}/invites/${enc(memberId.slice(INVITE_PREFIX.length))}`);
      else await r.del<void>(`/orgs/${enc(o)}/members/${enc(memberId)}`);
    },
    audit: async (limit = 200): Promise<AuditEntry[]> => (await r.get<unknown[]>(`/audit?limit=${limit}`)).map(normalizeAudit),
    /** Check for updates answered by the cloud (any agent version): newest release vs what the unit reported, and an
     * offer (stage only) when newer. Cloud only; the unit's own page asks the unit. */
    updateCheck: async (id: DeviceId, kind: 'app' | 'os' = 'app'): Promise<UpdateCheckOut> =>
      r.post<UpdateCheckOut>(`/devices/${enc(id)}/update-check?kind=${kind}`, {}),
    // ---- hotspot password (cloud-managed) --------------------------------------------------------------
    /** Organisation default every unit follows unless it has its own (admin / owner). */
    hotspotDefault: async (): Promise<HotspotDefault> => r.get<HotspotDefault>(`/orgs/${enc(await org())}/hotspot-default`),
    setHotspotDefault: async (password: string): Promise<HotspotDefault> => r.put<HotspotDefault>(`/orgs/${enc(await org())}/hotspot-default`, { password }),
    /** One unit's hotspot password as the cloud set it, and whether the unit has it yet. */
    unitHotspot: (id: DeviceId): Promise<UnitHotspot> => r.get<UnitHotspot>(`/devices/${enc(id)}/hotspot`),
    /** Its own password, or null to follow the organisation default again. */
    setUnitHotspot: (id: DeviceId, password: string | null): Promise<UnitHotspot> => r.put<UnitHotspot>(`/devices/${enc(id)}/hotspot`, { password }),
    // ---- presets (cloud preset manager) ----------------------------------------------------------------
    presets: async (): Promise<Preset[]> => (local ? [] : (await r.get<unknown[]>(`/presets`)).map(normalizePreset)),
    createPreset: async (body: PresetIn): Promise<Preset> => normalizePreset(await r.post(`/presets?org_id=${enc(await org())}`, body)),
    updatePreset: async (id: string, body: Partial<PresetIn>): Promise<Preset> => normalizePreset(await r.put(`/presets/${enc(id)}`, body)),
    deletePreset: async (id: string): Promise<void> => {
      await r.del<void>(`/presets/${enc(id)}`);
    },
    /** Save a unit's current settings (chosen sections) as a cloud preset. */
    capturePreset: async (body: { device_id: DeviceId; name: string; description?: string; sections: string[]; overwrite?: boolean }): Promise<Preset> =>
      normalizePreset(await r.post(`/presets/capture?org_id=${enc(await org())}`, body)),
    /** Copy the presets saved on a unit into the cloud library. */
    importPresets: async (body: { device_id: DeviceId; overwrite?: boolean }): Promise<{ imported: string[]; skipped: string[] }> =>
      r.post<{ imported: string[]; skipped: string[] }>(`/presets/import?org_id=${enc(await org())}`, body),
    applyPreset: async (id: string, body: { device_ids?: DeviceId[]; all?: boolean; save_on_unit?: boolean }): Promise<{ results: PresetApplyResult[] }> =>
      r.post<{ results: PresetApplyResult[] }>(`/presets/${enc(id)}/apply`, body),
    // ---- caption schedules (cloud only: the worker sends engine.stop / engine.start at the edges) ---------
    schedules: async (): Promise<CaptionSchedule[]> => (local ? [] : (await r.get<unknown[]>(`/schedules?org_id=${enc(await org())}`)).map(normalizeSchedule)),
    createSchedule: async (body: CaptionScheduleIn): Promise<CaptionSchedule> => normalizeSchedule(await r.post(`/schedules?org_id=${enc(await org())}`, body)),
    updateSchedule: async (id: string, body: CaptionScheduleIn): Promise<CaptionSchedule> => normalizeSchedule(await r.put(`/schedules/${enc(id)}`, body)),
    deleteSchedule: async (id: string): Promise<void> => {
      await r.del<void>(`/schedules/${enc(id)}`);
    },
    /** The schedule badge for one unit (local shell: no schedules). */
    deviceSchedule: async (id: DeviceId): Promise<CaptionScheduleState> =>
      local
        ? { device_id: id, desired: null, schedule_id: null, schedule_name: null, until: null, override: false, applied: false, last_error: null }
        : normalizeScheduleState(await r.get(`/schedules/device/${enc(id)}`)),
    // Events are a cloud resource (assignments); the device agent has none and its SPA fallback
    // would answer this path with index.html.
    events: (): Promise<ShowEvent[]> => (local ? Promise.resolve([]) : listEvents()),
    createEvent: async (body: ShowEventIn): Promise<ShowEvent> => normalizeEvent(await r.post(`/orgs/${enc(await org())}/events`, eventBody(body))),
    updateEvent: async (id: string, body: ShowEventIn): Promise<ShowEvent> => normalizeEvent(await r.put(`/orgs/${enc(await org())}/events/${enc(id)}`, eventBody(body))),
    deleteEvent: async (id: string): Promise<void> => {
      await r.del<void>(`/orgs/${enc(await org())}/events/${enc(id)}`);
    },
    /** Zero-touch units wait as `pending`; approving names them and puts them on a show (platform staff). */
    approveDevice: async (deviceId: DeviceId, body: { name?: string; event_id?: string | null; org_id?: string | null }): Promise<Device> =>
      normalizeFleetDevice(await r.post(`/devices/${enc(deviceId)}/approve`, { name: body.name || null, event_id: body.event_id ?? null, org_id: body.org_id ?? null })),
    rejectDevice: async (deviceId: DeviceId): Promise<Device> => normalizeFleetDevice(await r.post(`/devices/${enc(deviceId)}/reject`)),
    /** Single-use code typed on a unit's Cloud page; optionally pre-named and pre-assigned to a show. */
    mintClaimCode: async (body: { name?: string; event_id?: string | null; ttl_minutes?: number }): Promise<{ code: string; expires_at: number | null }> => {
      const o = await org();
      const raw = await r.post<{ code: string; expires_at?: string }>('/devices/claim-codes', { name: body.name || null, org_id: o, event_id: body.event_id ?? null, ttl_minutes: body.ttl_minutes ?? 60 });
      return { code: raw.code, expires_at: raw.expires_at ? Date.parse(raw.expires_at) / 1000 : null };
    },
    /** Put a device on a show (platform staff). `null` ends the current assignment. */
    assignDevice: async (deviceId: DeviceId, eventId: string | null): Promise<void> => {
      if (eventId === null) await r.post(`/devices/${enc(deviceId)}/unassign`);
      else await r.post(`/devices/${enc(deviceId)}/assign`, { org_id: await org(), event_id: eventId });
    },
  };
}
export type Api = ReturnType<typeof createApi>;

/** Show editor input: unix seconds in, ISO out to the API. */
export interface ShowEventIn {
  name: string;
  venue: string;
  description: string;
  tz: string;
  starts_at: number;
  ends_at: number;
}
const eventBody = (b: ShowEventIn) => ({
  name: b.name,
  venue: b.venue,
  description: b.description ?? '',
  tz: b.tz || 'UTC',
  starts_at: new Date(b.starts_at * 1000).toISOString(),
  ends_at: new Date(b.ends_at * 1000).toISOString(),
});
